/* C66: consumes the C3 owner's shared native /collections/all feed.
 * GET only. Close every page and validate one inventory before changing SSR.
 * This PDP adapter owns no duplicate catalogue feed and no home markup/CSS.
 */
(() => {
  'use strict';
  if (customElements.get('miniami-pdp-catalogue')) return;
  customElements.define('miniami-pdp-catalogue', class extends HTMLElement {
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      const options = { signal:this.life.signal };
      const refresh = () => this.refresh();
      this.querySelector('[data-pdp-pattern-retry]')?.addEventListener('click', refresh, options);
      window.addEventListener('pageshow', e => { if (e.persisted) refresh(); }, options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && Date.now() - (this.fetchedAt || 0) > 300000) refresh();
      }, options);
      this.refresh();
    }
    async refresh() {
      if (this.request || !this.isConnected) return;
      this.request = new AbortController();
      const current = this.request, deadline = setTimeout(() => current.abort(), 20000);
      const status = this.querySelector('[data-pdp-pattern-status]');
      const retry = this.querySelector('[data-pdp-pattern-retry]');
      this.dataset.coverage = 'loading'; this.setAttribute('aria-busy','true');
      if (retry) retry.hidden = true;
      try {
        const gift = this.dataset.role === 'gift_card';
        const feed = new URL(this.dataset.feed, location.origin);
        const section = gift ? this.dataset.sectionId : 'miniami-pattern-marquee-page';
        if (gift && (!section || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(feed.pathname))) throw new Error('gift-source-route');
        let next = feed, page = 0, total, pages, scanned = 0;
        const visited = new Set(), ids = new Set(), items = [];
        while (next) {
          if (next.origin !== location.origin || (gift ? next.pathname !== feed.pathname : !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/all\/?$/i.test(next.pathname)) || next.searchParams.get('section_id') !== section || visited.has(next.href)) throw new Error('source-route');
          visited.add(next.href);
          const response = await fetch(next, { method:'GET', credentials:'same-origin', cache:'no-store', signal:current.signal });
          if (!response.ok) throw new Error('source-response');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = doc.querySelector(gift ? '[data-miniami-gift-pattern-page]' : '[data-miniami-pattern-page]');
          if (!payload) throw new Error('source-payload');
          const data = JSON.parse(payload.textContent);
          if (data.version !== 1 || Number(data.theme) !== 205457097035 || !['main','published','unpublished'].includes(data.themeRole) || data.page !== page + 1 || !Array.isArray(data.items) || !Number.isInteger(data.total) || data.total < 0 || !Number.isInteger(data.pages) || data.pages < 1 || !Number.isInteger(data.scanned) || data.scanned < 0 || data.scanned > 250) throw new Error('source-identity');
          if (!page) { total = data.total; pages = data.pages; }
          if (total !== data.total || pages !== data.pages) throw new Error('source-drift');
          page = data.page; scanned += data.scanned;
          for (const item of data.items) {
            if (!/^\d+$/.test(item.id || '') || ids.has(item.id) || !['standalone_pattern','add_on'].includes(item.role) || typeof item.html !== 'string') throw new Error('source-role');
            const template = document.createElement('template'); template.innerHTML = item.html;
            const card = template.content.firstElementChild;
            if (gift) {
              if (template.content.children.length !== 1 || !card?.matches('li.miniami-pdp-drift__item') || card.dataset.shopifyProductId !== item.id || card.dataset.imagery !== 'transparent' || card.querySelector('script,iframe,form') || !card.querySelector('.miniami-pdp-drift__media img.miniami-catalogue-cutout[src]') || !card.querySelector('a.miniami-pdp-drift__card[href]')) throw new Error('gift-source-card');
              items.push(card); ids.add(item.id); continue;
            }
            if (template.content.children.length !== 1 || !card?.matches('a.miniami-home-editorial__product-card') || card.dataset.productId !== item.id || card.querySelector('script,iframe,form')) throw new Error('source-card');
            const image = card.querySelector('.miniami-home-editorial__product-media img');
            const meta = card.querySelector('.miniami-home-editorial__product-meta');
            if (!image?.getAttribute('src') || !meta?.querySelector('strong') || !meta.querySelector('span')) throw new Error('source-photo');
            const li = document.createElement('li'); li.className = 'miniami-pdp-drift__item grid__item';
            li.dataset.shopifyProductId = item.id; li.dataset.productHandle = card.dataset.productHandle || ''; li.dataset.imagery = 'photography';
            const link = document.createElement('a'); link.className = 'miniami-pdp-drift__card'; link.href = card.getAttribute('href');
            const media = document.createElement('span'); media.className = 'miniami-pdp-drift__media'; media.append(image);
            const details = document.createElement('span'); details.className = 'miniami-pdp-drift__meta';
            const title = document.createElement('span'); title.className = 'miniami-pdp-drift__title'; title.textContent = window.MiniamiTitleBase(meta.querySelector('strong').textContent);
            const price = document.createElement('span'); price.className = 'miniami-pdp-drift__price'; price.textContent = meta.querySelector('span').textContent;
            details.append(title,price); link.append(media,details); li.append(link); items.push(li); ids.add(item.id);
          }
          next = data.next ? new URL(data.next,location.origin) : null;
          if (next) next.searchParams.set('section_id',section);
        }
        if (scanned !== total || page !== pages || current.signal.aborted || !this.isConnected) throw new Error('source-incomplete');
        const drift = this.querySelector('miniami-pdp-drift'), primary = drift?.querySelector('[data-pdp-drift-primary]');
        if (!primary) throw new Error('source-SSR-shell');
        primary.replaceChildren(...items);
        // Reconnect the sole motion owner; its disposal/rebuild owns mirrors.
        drift.remove(); this.querySelector('[data-pdp-pattern-page]').prepend(drift);
        drift.dataset.patternCount = String(items.length);
        this.dataset.coverage = 'complete'; this.dataset.patternCount = String(items.length);
        this.dataset.sourceTotal = String(total); this.dataset.sourcePages = String(page);
        this.dataset.fetchedAt = new Date().toISOString(); this.fetchedAt = Date.now();
        if (status) { status.textContent = items.length ? '' : 'No patterns are available yet.'; status.hidden = Boolean(items.length); }
      } catch (_) {
        if (!this.isConnected || this.request !== current) return;
        this.dataset.coverage = 'incomplete';
        if (status) { status.textContent = 'Patterns could not be updated. Previously loaded patterns are still shown.'; status.hidden = false; }
        if (retry) retry.hidden = false;
      } finally {
        clearTimeout(deadline);
        if (this.request === current) { this.removeAttribute('aria-busy'); this.request = null; }
      }
    }
    disconnectedCallback() { this.life?.abort(); this.request?.abort(); this.life = null; this.request = null; }
  });
})();
